import { useEffect, useRef, useState } from 'react';
import type { QrDian } from '@tanteo/compartido';
import { parsearQrDian } from '@tanteo/compartido';

interface DetectorCodigo {
  detect(fuente: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats: string[] }) => DetectorCodigo;
  }
}

/**
 * Escáner del QR de documentos del proveedor (docs/04 §5, ADR-0004):
 * 1) el lector 2D teclea el contenido al input (vía principal en el mostrador),
 * 2) la cámara con BarcodeDetector nativo (si el navegador lo soporta).
 */
export function EscanerQr({ onQr }: { onQr: (parseado: QrDian, crudo: string) => void }) {
  const [camaraActiva, setCamaraActiva] = useState(false);
  const [soportaCamara] = useState(() => typeof window.BarcodeDetector === 'function');
  const [texto, setTexto] = useState('');
  const video = useRef<HTMLVideoElement>(null);
  const parar = useRef<() => void>(() => {});

  function procesar(crudo: string) {
    const limpio = crudo.trim();
    if (!limpio) return;
    onQr(parsearQrDian(limpio), limpio);
  }

  useEffect(() => {
    if (!camaraActiva || !video.current) return;
    let vivo = true;
    const el = video.current;
    const detector = new window.BarcodeDetector!({ formats: ['qr_code'] });
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (!vivo) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        el.srcObject = stream;
        void el.play();
        const intervalo = setInterval(() => {
          void detector
            .detect(el)
            .then((codigos) => {
              const valor = codigos[0]?.rawValue;
              if (valor) {
                procesar(valor);
                setCamaraActiva(false);
              }
            })
            .catch(() => {});
        }, 400);
        parar.current = () => {
          clearInterval(intervalo);
          stream.getTracks().forEach((t) => t.stop());
        };
      })
      .catch(() => setCamaraActiva(false));
    return () => {
      vivo = false;
      parar.current();
    };
  }, [camaraActiva]);

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-muted mb-1 block text-contenido font-semibold">
          Apunta el lector 2D al QR del documento del proveedor (teclea aquí solo) o pega el contenido:
        </span>
        {/* No es `AreaTexto`: el campo de escaneo lleva borde reforzado a propósito, para que el
            cajero vea de un vistazo dónde va a caer lo que teclee el lector. */}
        <textarea
          autoFocus
          rows={3}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              procesar(texto);
              setTexto('');
            }
          }}
          data-e2e="qr-texto"
          className="num rounded-control border-primary/60 w-full border-2 bg-card px-3 py-2 text-contenido outline-none focus:ring-2 focus:ring-primary/40"
          placeholder="El lector termina con Enter…"
        />
      </label>
      {soportaCamara && (
        <button
          onClick={() => setCamaraActiva((v) => !v)}
          className="rounded-control bg-surface w-full px-3 py-2 text-contenido font-bold"
        >
          {camaraActiva ? 'Apagar cámara' : '📷 Usar la cámara'}
        </button>
      )}
      {camaraActiva && <video ref={video} className="rounded-card w-full" muted playsInline />}
    </div>
  );
}
